import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Card, Container, Nav, Table } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import { addStatusToast, Loader, SectionHeader } from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { LOADING_STATUS, LOCALES } from '../../common/constants';
import {
  AccommodationAttributeEmptyData,
  AccommodationAttributeListItem,
  AccommodationAttributeQueryFilters,
  addAccommodationAttributeDataAsync,
  getAllAccommodationAttributesAsync,
  selectAccommodationAttributes,
  selectAccommodationAttributesLoadingStatus,
  UpdateAttributeIndexCommand,
  updateAttributeIndexesAsync,
  UpdateAttributeIndexItem,
} from './accommodationAttributesSlice';
import { updateAttributeIndexes } from '../../common/api/accommodationAttributesService';
import { getAllRolesAsync } from '../Users/identityUsersSlice';

function AccommodationAttributes() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const accommodationAttributes = useAppSelector(selectAccommodationAttributes);
  const accommodationAttributesLoadingStatus = useAppSelector(
    selectAccommodationAttributesLoadingStatus,
  );
  const [filtering, setFiltering] =
    useState<AccommodationAttributeQueryFilters>({
      pageSize: 200,
      pageNumber: 1,
    });

  const [attributesTemp, setAttributesTemp] = useState<
    AccommodationAttributeListItem[]
  >([]);
  // gets translated item based on keyId and locale
  // this is used in html to fetch proper translation of text
  function getItem(locale, keyId, item) {
    if (accommodationAttributesLoadingStatus === LOADING_STATUS.IDLE) {
      const found = item.translations.filter(
        a => a.locale === locale && a.keyId === keyId,
      );
      if (found && found[0] && found[0].text) {
        return found[0].text;
      }
    }
    return '';
  }

  useEffect(() => {
    if (accommodationAttributes != null) {
      setAttributesTemp(accommodationAttributes);
    }
  }, [accommodationAttributes]);
  const tableStyle = {
    border: '2px solid #D2DDEC',
    width: '100%',
  };
  const initFetch = useCallback(() => {
    dispatch(getAllAccommodationAttributesAsync(filtering));
  }, [dispatch]);
  useEffect(() => {
    initFetch();
  }, [initFetch]);
  // calls actual search, which calls internal getAllAccommodationAttributes Async
  // and that is then routed to backend
  const doSearch = event => {
    event.preventDefault();
    dispatch(getAllAccommodationAttributesAsync(filtering));
  };
  // Adds new Attribute with empty dataset
  // and then we route to edit that data.
  const addNew = event => {
    const newAccommodationAttributeData: AccommodationAttributeEmptyData = {
      add: '',
    };

    event.preventDefault();
    dispatch(
      addAccommodationAttributeDataAsync(newAccommodationAttributeData),
    ).then(resval =>
      navigate(`/accommodations/accommodationattributes/${resval.payload}`),
    );
  };
  const HandleAttributeItemReorder = (OldNumber: number, NewNumber: number) => {
    // Create a deep copy of the current attributes
    const reorderedAttributeList = accommodationAttributes.map(item => ({
      ...item,
    }));

    // Remove the item from the old position and insert it at the new position
    const [movedItem] = reorderedAttributeList.splice(OldNumber, 1);
    reorderedAttributeList.splice(NewNumber, 0, movedItem);
    // Update the 'index' property of each item in the reordered list
    reorderedAttributeList.forEach((item, index) => {
      item.index = index;
    });

    // Update the temporary state
    setAttributesTemp(reorderedAttributeList);

    // Prepare the data for the backend update
    const updateData = reorderedAttributeList.map(item => ({
      id: item.id,
      index: item.index,
    }));

    const data = { indexes: updateData } as UpdateAttributeIndexCommand;

    // Dispatch the update action
    dispatch(updateAttributeIndexesAsync(data)).then(() => {
      dispatch(getAllAccommodationAttributesAsync(filtering));
      dispatch(
        addStatusToast({
          Message: `Järjestys tallennettu`,
        }),
      );
    });
  };
  // generate html elements of listed accommodations
  // calling html element shoud have table element and header elements
  // as this only creates list of records
  const accommodationAttributeListItems = attributesTemp?.map(item => (
    <tr key={item.id}>
      <td>
        <Nav.Link
          as={Link}
          to={`/accommodations/accommodationattributes/${item.id}`}
          className="d-block"
        >
          {item.shortName !== '' ? item.shortName : 'Ei nimeä'}
        </Nav.Link>
      </td>
      <td>
        <Nav.Link
          as={Link}
          to={`/accommodations/accommodationattributes/${item.id}`}
          className="d-block"
        >
          {item.name !== '' ? item.name : 'Ei nimeä'}
        </Nav.Link>
      </td>
      <td>
        <Nav.Link
          as={Link}
          to={`/accommodations/accommodationattributes/${item.id}`}
          className="d-block"
        >
          {item.groupName ?? 'Ei nimeä'}
        </Nav.Link>
      </td>
      <td>
        <Button
          className="border-0 me-8 px-8"
          style={{ width: 48 }}
          disabled={item.index === 0}
          onClick={() => HandleAttributeItemReorder(item.index, item.index - 1)}
          title="Siirrä ylös"
          variant="outline-blue"
        >
          <Icon Name="caret-up-fill" />
        </Button>
        <Button
          className="border-0 me-8 px-8"
          style={{ width: 48 }}
          disabled={item.index + 1 === attributesTemp?.length}
          onClick={() => HandleAttributeItemReorder(item.index, item.index + 1)}
          title="Siirrä alas"
          variant="outline-blue"
        >
          <Icon Name="caret-down-fill" />
        </Button>
      </td>
    </tr>
  ));

  const AddNewButton: JSX.Element = (
    <Button variant="blue" className="ps-16" onClick={addNew}>
      <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
      Lisää uusi
    </Button>
  );

  // draws elements to browser
  return (
    <>
      <Helmet>
        <title>Varustelut - Haku</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Majoitukset ja Ryhmät']}
          Children={AddNewButton}
          Title="Varustelut"
        />

        <Card className="border shadow-none">
          <Loader
            IsLoading={
              accommodationAttributesLoadingStatus === LOADING_STATUS.LOADING
            }
          />
          {accommodationAttributesLoadingStatus === LOADING_STATUS.IDLE && (
            <>
              <div className="py-16 px-24 text-end">
                <Button
                  onClick={doSearch}
                  variant="outline-blue"
                  size="sm"
                  type="submit"
                >
                  Hae Varustelut
                </Button>
              </div>
              <div className="table-responsive-lg">
                <Table hover className="table-data">
                  <thead className="border-top">
                    <tr>
                      <th>Varustelun nimi</th>
                      <th>Kuvaus</th>
                      <th>Ryhmä</th>
                      <th>Järjestys</th>
                    </tr>
                  </thead>
                  <tbody>{accommodationAttributeListItems}</tbody>
                </Table>
              </div>
            </>
          )}
        </Card>
      </Container>
    </>
  );
}

export default AccommodationAttributes;
